import React, { useState, useEffect } from 'react';
import {
  X, BookOpen, Sparkles, Brain, Rocket, Shield, Target, Bug,
  FileText, Building2, Swords, Key, Server, AlertTriangle,
  Search, Activity, BarChart3, Lock, Network, ChevronRight,
  ChevronLeft, CheckCircle, Lightbulb
} from 'lucide-react';

/**
 * Simple Tutorial Modal Component
 * Shows a step-by-step guide based on the features
 */
const TutorialModal = ({ isOpen, onClose, title, description, features }) => {
  const [currentStep, setCurrentStep] = useState(0);

  if (!isOpen) return null;

  const totalSteps = features.length + 1; // +1 for intro

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-brand-black-light rounded-2xl border border-brand-green/30 w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-brand-green/20 bg-gradient-to-r from-brand-green/10 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-brand-green/20 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-brand-green" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Guide Rapide</h2>
                <p className="text-gray-400 text-sm">Discover the features of this page</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {Array.from({ length: totalSteps }).map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === currentStep
                    ? 'bg-brand-green w-6'
                    : idx < currentStep
                    ? 'bg-brand-green/50'
                    : 'bg-gray-600'
                }`}
              />
            ))}
          </div>

          {/* Step content */}
          <div className="bg-brand-black rounded-xl border border-gray-700 p-6 min-h-[200px]">
            {currentStep === 0 ? (
              // Introduction step
              <div>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-brand-green/20 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-7 h-7 text-brand-green" />
                  </div>
                  <div>
                    <span className="text-xs text-brand-green font-mono">Step 1/{totalSteps}</span>
                    <h3 className="text-xl font-bold text-white">{title}</h3>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed break-words">{description}</p>
                <div className="mt-4 bg-brand-green/10 rounded-lg p-4 border border-brand-green/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-5 h-5 text-brand-green" />
                    <span className="text-brand-green font-medium">Conseil</span>
                  </div>
                  <p className="text-gray-300 text-sm">
                    Browse the following steps to discover the main features of this page.
                  </p>
                </div>
              </div>
            ) : (
              // Feature steps
              (() => {
                const feature = features[currentStep - 1];
                const FeatureIcon = feature?.icon || Sparkles;
                return (
                  <div>
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-xl ${feature?.color || 'bg-brand-green/20'} flex items-center justify-center flex-shrink-0`}>
                        <FeatureIcon className={`w-7 h-7 ${feature?.iconColor || 'text-brand-green'}`} />
                      </div>
                      <div>
                        <span className="text-xs text-brand-green font-mono">Step {currentStep + 1}/{totalSteps}</span>
                        <h3 className="text-xl font-bold text-white">{feature?.title}</h3>
                      </div>
                    </div>
                    <p className="text-gray-300 leading-relaxed text-lg break-words">{feature?.description}</p>
                    {feature?.tips && (
                      <div className="mt-4 bg-brand-green/10 rounded-lg p-4 border border-brand-green/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Lightbulb className="w-5 h-5 text-brand-green" />
                          <span className="text-brand-green font-medium">Astuce</span>
                        </div>
                        <p className="text-gray-300 text-sm">{feature.tips}</p>
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>

            <span className="text-gray-400 text-sm">
              {currentStep + 1} / {totalSteps}
            </span>

            {currentStep < totalSteps - 1 ? (
              <button
                onClick={nextStep}
                className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2 font-medium"
              >
                Suivant
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2 font-medium"
              >
                Terminer
                <CheckCircle className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Reusable Welcome Banner Component
 * Used across different pages to provide consistent onboarding experience
 */
const WelcomeBanner = ({
  pageId,           // Unique ID for localStorage (e.g., 'scans', 'vulnerabilities')
  title,            // Main title
  description,      // Description text
  features = [],    // Array of { icon, title, description, color }
  onStartTutorial,  // Optional callback for tutorial button
  showTutorialButton = false
}) => {
  const [dismissed, setDismissed] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    const hasDismissed = localStorage.getItem(`${pageId}_welcome_dismissed`);
    if (hasDismissed) {
      setDismissed(true);
    }
  }, [pageId]);

  const handleDismiss = () => {
    localStorage.setItem(`${pageId}_welcome_dismissed`, 'true');
    setDismissed(true);
  };

  const handleStartTutorial = () => {
    if (onStartTutorial) {
      onStartTutorial();
    } else {
      setShowTutorial(true);
    }
  };

  if (dismissed) return null;

  return (
    <>
      <div className="bg-gradient-to-r from-brand-green/20 via-blue-500/10 to-purple-500/10 rounded-xl border border-brand-green/30 p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-brand-green/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-7 h-7 text-brand-green" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-2 truncate">{title}</h3>
              <p className="text-gray-300 mb-4 max-w-3xl break-words">{description}</p>
              <div className="flex items-center gap-3">
                {showTutorialButton && (
                  <button
                    onClick={handleStartTutorial}
                    className="px-4 py-2 bg-brand-green text-brand-black rounded-lg hover:bg-brand-green/90 transition-colors flex items-center gap-2 font-medium"
                  >
                    <BookOpen className="w-4 h-4" />
                    Suivre le tutoriel
                  </button>
                )}
                <button
                  onClick={handleDismiss}
                  className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  J'ai compris
                </button>
              </div>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Feature hints */}
        {features.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
            {features.map((feature, idx) => {
              const FeatureIcon = feature.icon;
              return (
                <div key={idx} className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${feature.color || 'bg-brand-green/20'}`}>
                    <FeatureIcon className={`w-5 h-5 ${feature.iconColor || 'text-brand-green'}`} />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-white font-medium text-sm">{feature.title}</h4>
                    <p className="text-gray-400 text-xs break-words">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Built-in Tutorial Modal */}
      <TutorialModal
        isOpen={showTutorial}
        onClose={() => setShowTutorial(false)}
        title={title}
        description={description}
        features={features}
      />
    </>
  );
};

// Pre-configured banners for each page
export const ScansWelcomeBanner = ({ onStartTutorial }) => (
  <WelcomeBanner
    pageId="scans"
    title="Bienvenue dans les Scans !"
    description="Launch security scans on your targets. Choose between autonomous AI mode, manual expert mode, or specialized (API, OT, IoT). Results automatically feed other modules."
    features={[
      { icon: Brain, title: 'Scan IA Autonome', description: "L'IA guide le scan automatiquement", color: 'bg-blue-500/20', iconColor: 'text-blue-400' },
      { icon: Target, title: 'Advanced Mode', description: 'Advanced configuration for experts', color: 'bg-purple-500/20', iconColor: 'text-purple-400' },
      { icon: Activity, title: 'Real Time', description: 'Follow progress live', color: 'bg-orange-500/20', iconColor: 'text-orange-400' }
    ]}
    onStartTutorial={onStartTutorial}
    showTutorialButton={true}
  />
);

export const VulnerabilitiesWelcomeBanner = ({ onStartTutorial }) => (
  <WelcomeBanner
    pageId="vulnerabilities"
    title="Welcome to Vulnerabilities!"
    description="Visualize and analyze all detected vulnerabilities. Prioritize with AI, generate automatic PoCs, and track possible attack paths."
    features={[
      { icon: Bug, title: 'Auto Detection', description: 'Vulnerabilities detected by scans', color: 'bg-red-500/20', iconColor: 'text-red-400' },
      { icon: Brain, title: 'Analyse IA', description: 'Priorisation intelligente des risques', color: 'bg-blue-500/20', iconColor: 'text-blue-400' },
      { icon: Rocket, title: 'Automatic PoC', description: 'Generate exploits in 1 click', color: 'bg-purple-500/20', iconColor: 'text-purple-400' }
    ]}
    onStartTutorial={onStartTutorial}
    showTutorialButton={true}
  />
);

export const CredentialsWelcomeBanner = ({ onStartTutorial }) => (
  <WelcomeBanner
    pageId="credentials"
    title="Bienvenue dans les Identifiants !"
    description="Manage credentials discovered during pentests. Test passwords, check dark web leaks, and track compromised access."
    features={[
      { icon: Key, title: 'Collected credentials', description: 'Credentials found automatically', color: 'bg-yellow-500/20', iconColor: 'text-yellow-400' },
      { icon: Search, title: 'Dark Web Search', description: 'Check for data leaks', color: 'bg-red-500/20', iconColor: 'text-red-400' },
      { icon: Lock, title: 'Strength Test', description: 'Evaluate password robustness', color: 'bg-blue-500/20', iconColor: 'text-blue-400' }
    ]}
    onStartTutorial={onStartTutorial}
    showTutorialButton={true}
  />
);

export const ADWelcomeBanner = ({ onStartTutorial }) => (
  <WelcomeBanner
    pageId="ad_pentest"
    title="Bienvenue dans le Pentest Active Directory !"
    description="Analyze and exploit Active Directory environments. Detect attack paths, excessive privileges, and vulnerable configurations."
    features={[
      { icon: Network, title: 'BloodHound', description: 'Cartographie des chemins d\'attaque', color: 'bg-red-500/20', iconColor: 'text-red-400' },
      { icon: Shield, title: 'Kerberoasting', description: 'Attaques sur les tickets Kerberos', color: 'bg-orange-500/20', iconColor: 'text-orange-400' },
      { icon: Server, title: 'DC Audit', description: 'Domain controller audit', color: 'bg-blue-500/20', iconColor: 'text-blue-400' }
    ]}
    onStartTutorial={onStartTutorial}
    showTutorialButton={true}
  />
);

export const AttackWelcomeBanner = ({ onStartTutorial }) => (
  <WelcomeBanner
    pageId="attack"
    title="Bienvenue dans la Matrice d'Attaque !"
    description="Visualize and execute attack techniques based on the MITRE ATT&CK framework. Plan your attack scenarios and track your coverage."
    features={[
      { icon: Swords, title: 'MITRE ATT&CK', description: 'Techniques categorized by tactic', color: 'bg-red-500/20', iconColor: 'text-red-400' },
      { icon: Target, title: 'Scenarios', description: 'Create attack chains', color: 'bg-purple-500/20', iconColor: 'text-purple-400' },
      { icon: BarChart3, title: 'Coverage', description: 'Track tested techniques', color: 'bg-brand-green/20', iconColor: 'text-brand-green' }
    ]}
    onStartTutorial={onStartTutorial}
    showTutorialButton={true}
  />
);

export const ReportsWelcomeBanner = ({ onStartTutorial }) => (
  <WelcomeBanner
    pageId="reports"
    title="Bienvenue dans les Rapports !"
    description="Generate professional pentest reports. Choose from several templates, customize the content, and export as PDF, DOCX or HTML."
    features={[
      { icon: FileText, title: 'Pro Templates', description: 'Executive and technical reports', color: 'bg-blue-500/20', iconColor: 'text-blue-400' },
      { icon: Brain, title: 'AI Summary', description: 'AI-generated summaries', color: 'bg-purple-500/20', iconColor: 'text-purple-400' },
      { icon: Shield, title: 'Compliance', description: 'Standards-compliant reports', color: 'bg-brand-green/20', iconColor: 'text-brand-green' }
    ]}
    onStartTutorial={onStartTutorial}
    showTutorialButton={true}
  />
);

export const ClientsWelcomeBanner = ({ onStartTutorial }) => (
  <WelcomeBanner
    pageId="clients"
    title="Bienvenue dans la Gestion des Clients !"
    description="Organize your pentest missions by client. Create dedicated workspaces, manage contracts, and track audit history."
    features={[
      { icon: Building2, title: 'Espaces clients', description: 'Un espace par client/mission', color: 'bg-blue-500/20', iconColor: 'text-blue-400' },
      { icon: FileText, title: 'Contracts', description: 'Legal document management', color: 'bg-purple-500/20', iconColor: 'text-purple-400' },
      { icon: Activity, title: 'Historique', description: 'Suivi de tous les audits', color: 'bg-brand-green/20', iconColor: 'text-brand-green' }
    ]}
    onStartTutorial={onStartTutorial}
    showTutorialButton={true}
  />
);

export default WelcomeBanner;
